import React from 'react';

export default function Terms() {
  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Términos y Condiciones</h1>
          <p className="text-gray-500">Última actualización: Septiembre 2026</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-10 space-y-8 text-gray-700 leading-relaxed text-sm">

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">1. Aceptación de los Términos</h2>
            <p>Al acceder y utilizar la plataforma U-CAN, aceptas cumplir con estos Términos y Condiciones. Si no estás de acuerdo con alguno de estos términos, te pedimos que no utilices nuestros servicios.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">2. Descripción del Servicio</h2>
            <p>U-CAN es una plataforma que conecta dueños de mascotas con cuidadores independientes. U-CAN actúa únicamente como intermediario y no es responsable directo de los servicios prestados por los cuidadores.</p>
            <p className="mt-2">Los servicios disponibles incluyen:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Paseo de perros</li>
              <li>Cuidado mientras sus dueños viajan</li>
              <li>Daycare cuidado diurno</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">3. Registro y Cuentas</h2>
            <p>Para utilizar ciertos servicios de U-CAN, debes crear una cuenta proporcionando información veraz y actualizada. Eres responsable de mantener la confidencialidad de tu cuenta y contraseña.</p>
            <p className="mt-2">Existen dos tipos de cuentas:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Cliente:</strong> Personas que buscan servicios de cuidado para sus mascotas.</li>
              <li><strong>Cuidador:</strong> Personas que ofrecen servicios de cuidado de mascotas.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">4. Suscripciones y Pagos</h2>

            <p>
              Para acceder a determinadas funciones Premium, los usuarios pueden adquirir
              una suscripción. En la aplicación U-CAN para iOS, las suscripciones digitales
              se procesan exclusivamente mediante el sistema In-App Purchase de Apple.
              En la versión web, los pagos pueden ser procesados mediante proveedores
              externos de pago.
            </p>

            <ul className="list-disc pl-6 mt-3 space-y-1">
              <li>El nombre, precio y duración de cada suscripción se muestran antes de confirmar la compra.</li>
              <li>Las suscripciones Premium de U-CAN para iOS tienen una duración de un mes y se renuevan automáticamente.</li>
              <li>El pago se carga a la cuenta de Apple del usuario al confirmar la compra.</li>
              <li>La suscripción se renueva automáticamente salvo que el usuario la cancele desde la configuración de suscripciones de su cuenta de Apple.</li>
              <li>La administración y cancelación de las suscripciones adquiridas mediante iOS se realiza a través de la cuenta de Apple del usuario.</li>
              <li>U-CAN se reserva el derecho de modificar los precios, respetando las condiciones y mecanismos de información establecidos por la plataforma de pago correspondiente.</li>
            </ul>

            <p className="mt-4">
              El uso de U-CAN en dispositivos Apple también está sujeto al{' '}
              <a
                href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#E6202E] font-semibold hover:underline"
              >
                Acuerdo de Licencia de Usuario Final (EULA) estándar de Apple
              </a>.
            </p>

            <p className="mt-2">
              Para conocer cómo tratamos tus datos personales, consulta nuestra{' '}
              <a
                href="/privacidad"
                className="text-[#E6202E] font-semibold hover:underline"
              >
                Política de Privacidad
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">5. Responsabilidades del Cuidador</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Proporcionar información veraz en su perfil.</li>
              <li>Cumplir con los servicios acordados con el cliente.</li>
              <li>Cuidar responsablemente de las mascotas a su cargo.</li>
              <li>Activar el protocolo de emergencia SOS disponible en la plataforma cuando ocurra una situación que pueda comprometer la salud o seguridad de la mascota.</li>
              <li>Mantener una comunicación clara y oportuna con los clientes.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">6. Responsabilidades del Cliente</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Proporcionar información veraz sobre sus mascotas raza, tamaño, comportamiento y salud.</li>
              <li>Informar al cuidador sobre cualquier necesidad especial de la mascota.</li>
              <li>Cumplir con los horarios y acuerdos establecidos con el cuidador.</li>
              <li>Respetar las condiciones y reglas del cuidador.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">7. Sistema de Calificaciones</h2>
            <p>U-CAN utiliza un sistema de calificación bidireccional y ciego:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Tanto clientes como cuidadores pueden calificarse mutuamente después de cada servicio.</li>
              <li>Las calificaciones permanecen ocultas hasta que ambas partes hayan enviado su evaluación, o hasta que transcurran 7 días.</li>
              <li>Este sistema garantiza reseñas honestas e imparciales.</li>
              <li>U-CAN se reserva el derecho de moderar y eliminar reseñas que incumplan nuestras políticas.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">8. Verificación de Cuidadores</h2>
            <p>U-CAN ofrece un sistema de verificación para cuidadores. Los cuidadores verificados han pasado por un proceso de revisión de antecedentes. Sin embargo, la verificación no constituye una garantía absoluta y los clientes deben ejercer su propio criterio.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">9. Limitación de Responsabilidad</h2>
            <p>U-CAN es una plataforma de intermediación y no se hace responsable de:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Daños, lesiones o pérdidas que puedan ocurrir durante la prestación de servicios entre usuarios.</li>
              <li>La calidad de los servicios prestados por los cuidadores.</li>
              <li>Disputas entre clientes y cuidadores.</li>
            </ul>
            <p className="mt-2">Recomendamos a los usuarios tomar las precauciones necesarias y verificar la información de los cuidadores antes de contratar sus servicios.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">10. Privacidad y Datos Personales</h2>
            <p>U-CAN se compromete a proteger la privacidad de sus usuarios. Los datos personales de los cuidadores nombre completo, teléfono y dirección solo son visibles para usuarios con suscripción activa. Para más información, consulta nuestra Política de Privacidad.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">11. Modificaciones</h2>
            <p>U-CAN se reserva el derecho de modificar estos Términos y Condiciones en cualquier momento. Los cambios serán notificados a través de la plataforma y entrarán en vigencia desde su publicación.</p>
          </section>

          <section>
  <h2 className="text-xl font-bold text-gray-900 mb-3">12. Soporte Veterinario de Emergencia</h2>

  <h3 className="font-semibold text-gray-900 mb-2">12.1 ¿Cómo funciona?</h3>
  <p>
    El Soporte Veterinario de U-Can consiste en un canal de comunicación e intermediación remota para brindar asistencia, orientación y guía frente a eventualidades o dudas de salud que surjan exclusivamente durante la ejecución de un servicio contratado (Paseo, Alojamiento o Daycare) a través de nuestra plataforma.
  </p>
  <p className="mt-2">
    <strong>Condición de Acceso:</strong> Este servicio de soporte es un beneficio exclusivo para el Cuidador a cargo del servicio, siempre y cuando mantenga activa su suscripción a la cuenta Premium de la plataforma. El sistema validará automáticamente el estado de la cuenta del cuidador al momento de activarse la alerta.
  </p>

  <h3 className="font-semibold text-gray-900 mt-6 mb-2">12.2 Tipo de soporte</h3>
  <p>
    El soporte se define estrictamente como una Asesoría Veterinaria Telefónica y/o Digital de Emergencia. Un profesional del área veterinaria indicará las acciones de primeros auxilios y medidas de contención que se pueden realizar en el momento del incidente mientras se gestiona el traslado a un centro de atención física si fuera necesario.
  </p>

  <h3 className="font-semibold text-gray-900 mt-6 mb-2">12.3 Horarios del servicio</h3>
  <p>
    Este canal de asesoría veterinaria en tiempo real estará operativo de lunes a domingo, desde las 08:00 am hasta las 20:00 pm. Fuera de este horario, las emergencias seguirán el protocolo general de comunicación directa con el tutor o derivación autónoma a centros asistenciales de urgencia.
  </p>

  <h3 className="font-semibold text-gray-900 mt-6 mb-2">12.4 ¿Qué tipo de emergencias contempla?</h3>
  <p>
    El soporte contempla orientación exclusiva para incidentes imprevistos ocurridos durante la vigencia del servicio, tales como:
  </p>

  <ul className="list-disc pl-6 mt-2 space-y-1">
    <li>Lesiones físicas leves o agudas sufridas en la ruta del paseo, por ejemplo cortes en almohadillas, cojeras repentinas o torceduras.</li>
    <li>Signos de fatiga extrema, golpes de calor o deshidratación.</li>
    <li>Reacciones alérgicas agudas o picaduras de insectos durante el trayecto.</li>
    <li>Ingesta sospechosa de elementos ajenos en la vía pública.</li>
  </ul>

  <h3 className="font-semibold text-gray-900 mt-6 mb-2">12.5 ¿Cómo se activa el soporte en tiempo real y paso a paso del canal de emergencia?</h3>
  <p>
    En caso de ocurrir un incidente durante el servicio, el cuidador premium debe seguir este flujo directo en la plataforma para activar la asistencia:
  </p>

  <ul className="list-disc pl-6 mt-2 space-y-1">
    <li><strong>Ingresar a la plataforma:</strong> Entrar al sitio web oficial u-can.cl.</li>
    <li><strong>Acceder al panel:</strong> Dirigirse a la sección "Panel de Cuidador".</li>
    <li><strong>Activar SOS:</strong> En la parte inferior de dicho panel, encontrará un apartado de color rojo que dice "Emergencia SOS". Al presionar este botón para Activar SOS, el sistema registrará la alerta y lo redireccionará de forma automática e inmediata a la aplicación de WhatsApp para iniciar el chat con nuestro soporte veterinario directo.</li>
    <li><strong>Notificación al tutor:</strong> Paralelamente, el cuidador tiene la obligación estricta de notificar a la brevedad al dueño o tutor de la mascota sobre el evento en desarrollo.</li>
  </ul>

  <p className="mt-4">
    Cualquier gasto derivado de consultas presenciales, exámenes, medicamentos, cirugías, procedimientos o traslados en clínicas externas será de responsabilidad exclusiva del tutor de la mascota o del cuidador a cargo, según corresponda legalmente por los acuerdos particulares entre las partes o la naturaleza del incidente. U-Can queda exento de cualquier cobro o disputa financiera médica. Asimismo, este soporte no cubre, bajo ninguna circunstancia, asesorías ni complicaciones derivadas de enfermedades preexistentes, crónicas o patologías previas al inicio del servicio contratado.
  </p>

  <p className="mt-4">
    <strong>DECLARACIÓN DE LIMITACIÓN DE RESPONSABILIDAD:</strong> El servicio de Soporte Veterinario de U-Can constituye una guía de orientación, primeros auxilios y asesoramiento remoto de buena fe. U-Can NO es un servicio de atención médica de urgencia clínica, NO cuenta con centros de hospitalización físicos propios y NO opera como un seguro de cobertura económica de gastos veterinarios.
  </p>
</section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">13. Contacto</h2>
            <p>Para consultas sobre estos Términos y Condiciones, puedes contactarnos en:</p>
            <p className="mt-2 font-medium text-[#E6202E]">hola@u-can.cl</p>
          </section>

        </div>
      </div>
    </div>
  );
}